<template>
  <EmailNotificationsTabSkeleton v-if="showSkeleton" variant="activity" />
  <div v-else class="automations-scroll-panel automations-scroll-panel--activity" :data-testid="dataTestidPrefix">
    <div class="fc-notification-toolbar fc-notification-toolbar--activity">
      <div class="fc-notification-toolbar__field fc-notification-toolbar__field--automation">
        <p class="fc-notification-toolbar__field-label">Automation</p>
        <div class="fc-notification-toolbar__field-control">
          <SearchableMultiSelectDropdown
            :value="uiFilters.automationIds"
            :items="automationOptions"
            item-text="label"
            item-value="value"
            label=""
            placeholder="All automations"
            max-activator-width="100%"
            menu-width="15.625rem"
            :disabled="isAutomationFilterLocked"
            :data-testid-prefix="`${dataTestidPrefix}__automation`"
            @input="onAutomationFilter"
          />
        </div>
      </div>
      <div class="fc-notification-toolbar__field fc-notification-toolbar__field--source">
        <p class="fc-notification-toolbar__field-label">Source</p>
        <div class="fc-notification-toolbar__field-control">
          <SearchableMultiSelectDropdown
            :value="uiFilters.sources"
            :items="sourceOptions"
            item-text="label"
            item-value="value"
            label=""
            placeholder="All sources"
            max-activator-width="100%"
            menu-width="14rem"
            :data-testid-prefix="`${dataTestidPrefix}__source`"
            @input="onSourcesFilter"
          />
        </div>
      </div>
      <div class="fc-notification-toolbar__field fc-notification-toolbar__field--outcome">
        <p class="fc-notification-toolbar__field-label">Outcome</p>
        <div class="fc-notification-toolbar__field-control">
          <SearchableMultiSelectDropdown
            :value="uiFilters.outcomes"
            :items="outcomeOptions"
            item-text="label"
            item-value="value"
            label=""
            placeholder="All outcomes"
            max-activator-width="100%"
            menu-width="9.375rem"
            :data-testid-prefix="`${dataTestidPrefix}__outcome`"
            @input="onOutcomesFilter"
          />
        </div>
      </div>
      <div class="fc-notification-toolbar__field fc-notification-toolbar__field--cycle">
        <p class="fc-notification-toolbar__field-label">Cycle</p>
        <div class="fc-notification-toolbar__field-control">
          <SearchableMultiSelectDropdown
            :value="uiFilters.cycleIds"
            :items="cycleOptions"
            item-text="label"
            item-value="value"
            label=""
            placeholder="All cycles"
            max-activator-width="100%"
            menu-width="10.625rem"
            :data-testid-prefix="`${dataTestidPrefix}__cycle`"
            @input="onCycleFilter"
          />
        </div>
      </div>
      <div class="fc-notification-toolbar__field fc-notification-toolbar__field--period">
        <p class="fc-notification-toolbar__field-label">Date range</p>
        <div class="fc-notification-toolbar__field-control">
          <PeriodRangePicker
            :from="uiFilters.dateFrom"
            :to="uiFilters.dateTo"
            label=""
            placeholder="All dates"
            :data-testid-prefix="`${dataTestidPrefix}__date`"
            @input="onDateRange"
          />
        </div>
      </div>
      <span class="fc-notification-toolbar__spacer" aria-hidden="true" />
      <v-btn
        v-if="hasActiveFilters"
        text
        class="fc-notification-toolbar__clear text-none"
        :data-testid="`${dataTestidPrefix}__clear-filters`"
        @click="clearFilters"
      >
        Clear filters
      </v-btn>
    </div>

    <div
      class="fc-activity-table-wrap"
      :class="{ 'fc-activity-table-wrap--loading': showTableLoader }"
      :data-testid="`${dataTestidPrefix}__table-wrap`"
    >
      <v-progress-linear
        v-if="showTableLoader"
        indeterminate
        absolute
        color="primary"
        class="fc-activity-table-wrap__progress"
        :data-testid="`${dataTestidPrefix}__loading`"
      />
      <table class="fc-activity-table" :data-testid="`${dataTestidPrefix}__table`">
        <thead>
          <tr>
            <th v-if="canExpand" class="fc-activity-table__col-expand" scope="col" />
            <th scope="col" class="fc-activity-table__col-time">{{ timeColumnLabel }}</th>
            <th scope="col">Automation</th>
            <th scope="col">Source</th>
            <th scope="col">Cycle</th>
            <th scope="col">Emails</th>
            <th scope="col">Outcome</th>
          </tr>
        </thead>
        <tbody v-if="rows.length">
          <template v-for="item in rows">
            <tr
              :key="item.run_id"
              class="fc-activity-run"
              :class="{ 'fc-activity-run--expanded': isExpanded(item) }"
              :data-testid="`${dataTestidPrefix}__run-${item.run_id}`"
            >
              <td v-if="canExpand" class="fc-activity-table__col-expand">
                <button
                  v-if="hasChildren(item)"
                  type="button"
                  class="fc-activity-expand-btn"
                  :aria-expanded="isExpanded(item)"
                  :data-testid="`${dataTestidPrefix}__expand-${item.run_id}`"
                  @click="toggleExpand(item)"
                >
                  <v-icon small>{{ isExpanded(item) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                </button>
              </td>
              <td class="fc-activity-table__col-time">{{ formatRunAt(item.at) }}</td>
              <td>
                <div class="fc-notification-name">
                  <div class="fc-notification-name__title">{{ automationName(item) }}</div>
                </div>
              </td>
              <td>
                <span
                  class="fc-activity-source"
                  :class="`fc-activity-source--${item.source_kind || 'notification'}`"
                >
                  {{ sourceLabel(item) }}
                </span>
              </td>
              <td class="fc-activity-table__nowrap">{{ cycleLabel(item) }}</td>
              <td :class="{ 'fc-activity-table__muted': !(item.emails > 0) }">{{ emailsLabel(item) }}</td>
              <td>
                <span class="fc-activity-chip" :class="`fc-activity-chip--${outcomeChip(item).variant}`">
                  <v-icon v-if="outcomeChip(item).variant === 'success'" x-small class="mr-1">mdi-check</v-icon>
                  <span v-if="outcomeChip(item).variant === 'neutral'" class="fc-activity-chip__dot" />
                  {{ outcomeChip(item).text }}
                </span>
              </td>
            </tr>
            <template v-if="canExpand && isExpanded(item) && hasChildren(item)">
              <tr :key="`${item.run_id}-child-head`" class="fc-activity-child-head">
                <td v-if="canExpand" />
                <td colspan="3">Recipient</td>
                <td colspan="2">Cycles</td>
                <td>Status</td>
              </tr>
              <tr
                v-for="(child, childIndex) in visibleChildren(item)"
                :key="`${item.run_id}-child-${childIndex}`"
                class="fc-activity-child"
              >
                <td v-if="canExpand" />
                <td colspan="3">
                  <span class="fc-activity-recipient">
                    <v-icon x-small class="fc-activity-recipient__icon">mdi-account-outline</v-icon>
                    {{ recipientLine(child) }}
                  </span>
                </td>
                <td colspan="2">{{ childCyclesLabel(child) }}</td>
                <td>
                  <span class="fc-activity-chip" :class="`fc-activity-chip--${childStatusChip(child).variant}`">
                    <v-icon v-if="childStatusChip(child).icon" x-small class="mr-1">{{ childStatusChip(child).icon }}</v-icon>
                    {{ childStatusChip(child).text }}
                  </span>
                  <span v-if="child.error" class="fc-activity-child__error">{{ child.error }}</span>
                </td>
              </tr>
              <tr
                v-if="showAllEmailsLink(item)"
                :key="`${item.run_id}-show-all`"
                class="fc-activity-child fc-activity-child--more"
              >
                <td :colspan="canExpand ? 7 : 6">
                  <button
                    type="button"
                    class="fc-activity-show-all"
                    :data-testid="`${dataTestidPrefix}__show-all-${item.run_id}`"
                    @click="showAllEmails(item)"
                  >
                    Show all {{ childCount(item) }} emails
                  </button>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
        <tbody v-else>
          <tr class="fc-activity-empty">
            <td :colspan="canExpand ? 7 : 6">No runs in this period</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="total > 0" class="fc-activity-pager">
      <span class="fc-activity-pager__range" :data-testid="`${dataTestidPrefix}__range`">{{ showingRange }}</span>
      <Pagination
        v-if="total > pageSize"
        :total="total"
        :page="page"
        :itemsperpage="pageSize"
        @paginated-data="onPage"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Pagination from '../../Pagination/Pagination.vue';
import EmailNotificationsTabSkeleton from '../EmailNotificationsTabSkeleton/EmailNotificationsTabSkeleton.vue';
import SearchableMultiSelectDropdown from '../../SearchableMultiSelectDropdown/SearchableMultiSelectDropdown.vue';
import PeriodRangePicker from '../../PeriodRangePicker/PeriodRangePicker.vue';
import {
  EMAIL_ACTIVITY_LOG_LEVEL,
  NOTIFICATION_ACTIVITY_CHILD_PREVIEW,
  NOTIFICATION_ACTIVITY_OUTCOME_OPTIONS,
  NOTIFICATION_ACTIVITY_SOURCE_OPTIONS,
} from '../../../utils/constants';
import {
  activityChildStatusChip,
  activityCycleLabel,
  activityEmailsLabel,
  activityOutcomeChip,
  activityRecipientLine,
  activitySourceLabel,
  formatActivityRunAt,
  shortTimezoneAbbr,
} from '../../../utils/notificationActivityUtils';
import { storedNotificationName } from '../../../utils/notificationNameUtils';

export default {
  name: 'NotificationActivityTable',
  components: {
    EmailNotificationsTabSkeleton,
    Pagination,
    SearchableMultiSelectDropdown,
    PeriodRangePicker,
  },
  props: {
    scope: { type: String, default: 'setup' },
    level: { type: String, default: 'detailed' },
    notificationId: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    dataTestidPrefix: { type: String, default: 'notification-activity-table' },
  },
  data() {
    return {
      page: 1,
      pageSize: 25,
      uiFilters: {
        automationIds: [],
        sources: [],
        outcomes: [],
        cycleIds: [],
        dateFrom: undefined,
        dateTo: undefined,
      },
      expandedRunIds: {},
      showAllRunIds: {},
      hasCompletedActivityLoad: false,
      /** Deep-link from “View run history”; cleared when user clicks Clear filters. */
      scopedNotificationActive: false,
      sourceOptions: NOTIFICATION_ACTIVITY_SOURCE_OPTIONS,
      outcomeOptions: NOTIFICATION_ACTIVITY_OUTCOME_OPTIONS,
    };
  },
  computed: {
    isAutomationFilterLocked() {
      return this.scopedNotificationActive && !!this.notificationId;
    },
    ...mapGetters('emailNotifications', ['activityLogs', 'fetchAllCreatedNotifications']),
    cyclesForSwitcher() {
      const fc = this.$store.state.m_financialclose || {};
      return fc.cyclesForSwitcher || [];
    },
    createdNotifications() {
      return this.fetchAllCreatedNotifications || [];
    },
    effectiveLevel() {
      return this.activityLogs?.level || this.level;
    },
    canExpand() {
      return this.effectiveLevel === EMAIL_ACTIVITY_LOG_LEVEL.DETAILED;
    },
    timeColumnLabel() {
      return `Time (${shortTimezoneAbbr()})`;
    },
    rows() {
      return this.activityLogs.items || [];
    },
    total() {
      return this.activityLogs.total || 0;
    },
    isActivityLoading() {
      return this.loading;
    },
    showSkeleton() {
      return !this.hasCompletedActivityLoad && this.isActivityLoading;
    },
    showTableLoader() {
      return this.hasCompletedActivityLoad && this.isActivityLoading;
    },
    automationOptions() {
      return (this.createdNotifications || [])
        .filter((doc) => doc.name)
        .map((doc) => ({ label: doc.name, value: String(doc._id) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    automationById() {
      const map = {};
      (this.createdNotifications || []).forEach((doc) => {
        map[String(doc._id)] = doc;
      });
      return map;
    },
    cycleOptions() {
      return (this.cyclesForSwitcher || [])
        .map((cycle) => ({
          label: cycle.description || cycle.name || cycle.cycle_name || 'Cycle',
          value: String(cycle._id),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    hasActiveFilters() {
      const f = this.uiFilters;
      return (
        this.isAutomationFilterLocked
        || (f.automationIds || []).length > 0
        || (f.sources || []).length > 0
        || (f.outcomes || []).length > 0
        || (f.cycleIds || []).length > 0
        || !!f.dateFrom
        || !!f.dateTo
      );
    },
    showingRange() {
      if (!this.total) return '';
      const start = (this.page - 1) * this.pageSize + 1;
      const end = Math.min(this.page * this.pageSize, this.total);
      return `Showing ${start}–${end} of ${this.total} runs`;
    },
  },
  watch: {
    notificationId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.scopedNotificationActive = true;
          this.uiFilters.automationIds = [String(id)];
        } else {
          this.scopedNotificationActive = false;
        }
        this.page = 1;
        this.load();
      },
    },
  },
  mounted() {
    this.$store.dispatch('fetchCyclesForSwitcher');
  },
  methods: {
    ...mapActions('emailNotifications', ['fetchNotificationActivityLogs']),
    formatRunAt: formatActivityRunAt,
    cycleLabel: activityCycleLabel,
    emailsLabel: activityEmailsLabel,
    outcomeChip: activityOutcomeChip,
    sourceLabel: activitySourceLabel,
    recipientLine: activityRecipientLine,
    childStatusChip(child) {
      return activityChildStatusChip(child?.status);
    },
    automationName(item) {
      const match = this.automationById[String(item.notification_id)];
      const name = match ? storedNotificationName(match) : storedNotificationName(item);
      return name || '—';
    },
    childCyclesLabel(child) {
      if (child?.cycles_label) return child.cycles_label;
      const cycles = child?.cycles;
      if (Array.isArray(cycles) && cycles.length) {
        const names = cycles.map((cycle) => cycle?.name).filter(Boolean);
        if (names.length) return names.join(', ');
      }
      return '—';
    },
    hasChildren(item) {
      return Array.isArray(item.children) && item.children.length > 0;
    },
    childCount(item) {
      return (item.children || []).length;
    },
    isExpanded(item) {
      return Boolean(this.expandedRunIds[item.run_id]);
    },
    isShowingAll(item) {
      return Boolean(this.showAllRunIds[item.run_id]);
    },
    visibleChildren(item) {
      const children = item.children || [];
      if (this.isShowingAll(item) || children.length <= NOTIFICATION_ACTIVITY_CHILD_PREVIEW) {
        return children;
      }
      return children.slice(0, NOTIFICATION_ACTIVITY_CHILD_PREVIEW);
    },
    showAllEmailsLink(item) {
      const count = this.childCount(item);
      return count > NOTIFICATION_ACTIVITY_CHILD_PREVIEW && !this.isShowingAll(item);
    },
    toggleExpand(item) {
      const id = item.run_id;
      if (!id) return;
      this.$set(this.expandedRunIds, id, !this.expandedRunIds[id]);
    },
    showAllEmails(item) {
      const id = item.run_id;
      if (!id) return;
      this.$set(this.showAllRunIds, id, true);
    },
    buildApiFilters() {
      const filters = {};
      const { automationIds, sources, outcomes, cycleIds, dateFrom, dateTo } = this.uiFilters;

      if (this.scopedNotificationActive && this.notificationId) {
        filters.notificationId = this.notificationId;
      } else if ((automationIds || []).length === 1) {
        filters.notificationId = automationIds[0];
      } else if ((automationIds || []).length > 1) {
        const features = automationIds
          .map((id) => this.automationById[id]?.feature)
          .filter(Boolean);
        if (features.length) filters.feature = [...new Set(features)];
      }

      if ((sources || []).length) filters.source = [...sources];
      if ((outcomes || []).length) filters.status = [...outcomes];
      if ((cycleIds || []).length === 1) filters.cycleId = cycleIds[0];
      if (dateFrom) filters.from = dateFrom;
      if (dateTo) filters.to = dateTo;
      return filters;
    },
    async load() {
      try {
        await this.fetchNotificationActivityLogs({
          page: this.page,
          pageSize: this.pageSize,
          filters: this.buildApiFilters(),
        });
      } finally {
        this.hasCompletedActivityLoad = true;
      }
    },
    resetPageAndLoad() {
      this.page = 1;
      this.load();
    },
    onPage({ page }) {
      this.page = page;
      this.load();
    },
    onAutomationFilter(value) {
      this.uiFilters.automationIds = value || [];
      this.resetPageAndLoad();
    },
    onSourcesFilter(value) {
      this.uiFilters.sources = value || [];
      this.resetPageAndLoad();
    },
    onOutcomesFilter(value) {
      this.uiFilters.outcomes = value || [];
      this.resetPageAndLoad();
    },
    onCycleFilter(value) {
      this.uiFilters.cycleIds = value || [];
      this.resetPageAndLoad();
    },
    onDateRange({ from, to }) {
      this.uiFilters.dateFrom = from || undefined;
      this.uiFilters.dateTo = to || undefined;
      this.resetPageAndLoad();
    },
    clearFilters() {
      this.scopedNotificationActive = false;
      this.uiFilters = {
        automationIds: [],
        sources: [],
        outcomes: [],
        cycleIds: [],
        dateFrom: undefined,
        dateTo: undefined,
      };
      this.$emit('clear-notification-scope');
      this.resetPageAndLoad();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './NotificationActivityTable.scss';
</style>
